/**
 * Raw Products Hooks
 *
 * React Query hooks for Raw Products data fetching.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  rawProductsService,
  type QueryRawProductsDto,
  type CreateRawProductDto,
  type UpdateRawProductDto,
} from '../services/raw-products.service';

/**
 * Hook to fetch paginated raw products list
 */
export function useRawProducts(params: QueryRawProductsDto, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['raw-products', params],
    queryFn: () => rawProductsService.list(params),
    placeholderData: (previousData) => previousData,
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch raw product by ID
 */
export function useRawProduct(id: string | null) {
  return useQuery({
    queryKey: ['raw-products', id],
    queryFn: () => rawProductsService.getById(id!),
    enabled: !!id,
  });
}

/**
 * Hook to fetch raw products statistics
 */
export function useRawProductStats() {
  return useQuery({
    queryKey: ['raw-products', 'stats'],
    queryFn: () => rawProductsService.getStats(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
}

/**
 * Hook to check SKU availability
 */
export function useCheckSku(sku: string, enabled: boolean = true) {
  return useQuery({
    queryKey: ['raw-products', 'check-sku', sku],
    queryFn: () => rawProductsService.checkSku(sku),
    enabled: enabled && sku.length >= 2,
    staleTime: 0, // Always refetch
  });
}

/**
 * Hook to create a raw product
 */
export function useCreateRawProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateRawProductDto) => rawProductsService.create(data),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['raw-products'] });
      toast.success(`Produit "${product.name}" cree avec succes`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la creation du produit';
      toast.error(message);
    },
  });
}

/**
 * Hook to update a raw product
 */
export function useUpdateRawProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRawProductDto }) =>
      rawProductsService.update(id, data),
    onSuccess: (product) => {
      queryClient.invalidateQueries({ queryKey: ['raw-products'] });
      queryClient.invalidateQueries({ queryKey: ['raw-products', product.id] });
      toast.success(`Produit "${product.name}" mis a jour`);
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? 'Erreur lors de la mise a jour';
      toast.error(message);
    },
  });
}

/**
 * Hook to archive a raw product
 */
export function useArchiveRawProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => rawProductsService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['raw-products'] });
      toast.success('Produit archive avec succes');
    },
    onError: (error: Error & { response?: { data?: { error?: { message?: string } } } }) => {
      const message = error.response?.data?.error?.message ?? "Erreur lors de l'archivage";
      toast.error(message);
    },
  });
}

/**
 * Hook to fetch raw products for select dropdowns (includes recipeUnit)
 */
export function useRawProductsForSelect() {
  return useQuery({
    queryKey: ['raw-products', 'for-select'],
    queryFn: async () => {
      // Fetch all active products
      const data = await rawProductsService.list({
        page: 1,
        limit: 1000, // Get all for select
        isActive: true,
        sortBy: 'name',
        sortOrder: 'asc',
      });
      return data.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch purchase prices for a raw product
 * @see US-8.11 - Prix d'Achat dans Drawer Produit
 */
export function usePurchasePrices(productId: string | null) {
  return useQuery({
    queryKey: ['raw-products', productId, 'purchase-prices'],
    queryFn: () => rawProductsService.getPurchasePrices(productId!),
    enabled: !!productId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}
